
import React, { createContext, useState, useEffect, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { compare } from 'compare-versions';

// A type of promise-like that resolves synchronously and supports only one observer

//const _iteratorSymbol = /*#__PURE__*/ typeof Symbol !== "undefined" ? (Symbol.iterator || (Symbol.iterator = Symbol("Symbol.iterator"))) : "@@iterator";

//const _asyncIteratorSymbol = /*#__PURE__*/ typeof Symbol !== "undefined" ? (Symbol.asyncIterator || (Symbol.asyncIterator = Symbol("Symbol.asyncIterator"))) : "@@asyncIterator";

// Asynchronously call a function and send errors to recovery continuation
function _catch(body, recover) {
  try {
    var result = body();
  } catch (e) {
    return recover(e);
  }
  if (result && result.then) {
    return result.then(void 0, recover);
  }
  return result;
}

var CacheBusterContext = createContext({
  checkCacheStatus: function checkCacheStatus() { }
});

function MyCacheBuster(_ref) {
  var _ref$children = _ref.children,
    children = _ref$children === void 0 ? null : _ref$children,
    currentVersion = _ref.currentVersion,
    _ref$isEnabled = _ref.isEnabled,
    isEnabled = _ref$isEnabled === void 0 ? false : _ref$isEnabled,
    _ref$isVerboseMode = _ref.isVerboseMode,
    isVerboseMode = _ref$isVerboseMode === void 0 ? false : _ref$isVerboseMode,
    _ref$loadingComponent = _ref.loadingComponent,
    loadingComponent = _ref$loadingComponent === void 0 ? null : _ref$loadingComponent,
    _ref$metaFileDirector = _ref.metaFileDirectory,
    metaFileDirectory = _ref$metaFileDirector === void 0 ? null : _ref$metaFileDirector,
    _ref$reloadOnDowngrad = _ref.reloadOnDowngrade,
    reloadOnDowngrade = _ref$reloadOnDowngrad === void 0 ? false : _ref$reloadOnDowngrad,
    onCacheClear = _ref.onCacheClear;

  var _useState = useState({
    loading: true,
    isLatestVersion: false
  }),
    cacheStatus = _useState[0],
    setCacheStatus = _useState[1];

  var log = function log(message, isError) {
    isVerboseMode && (isError ? console.error(message) : console.log(message));
  };

  useEffect(function () {
    isEnabled ? checkCacheStatus() : log('React Cache Buster is disabled.');
  }, []);

  var getMetaFileDirectory = function getMetaFileDirectory() {
    return !metaFileDirectory || metaFileDirectory === '.' ? '' : metaFileDirectory;
  };

  var checkCacheStatus = useCallback(function () {
    try {
      var _temp2 = _catch(function () {
        return Promise.resolve(fetch(getMetaFileDirectory() + "/meta.json?ver=" + Date.now())).then(function (res) {
          return Promise.resolve(res.json()).then(function (_ref2) {
            var metaVersion = _ref2.version;
            var shouldForceRefresh = isThereNewVersion(metaVersion, currentVersion);

            if (shouldForceRefresh) {
              log("There is a new version (v" + metaVersion + "). Should force refresh.");
              log('metaVersion=' + metaVersion);
              log('currentVersion=' + currentVersion);
              setCacheStatus({
                loading: false,
                isLatestVersion: false
              });
            } else {
              log('There is no new version. No cache refresh needed.');
              log('metaVersion=' + metaVersion);
              log('currentVersion=' + currentVersion);
              setCacheStatus({
                loading: false,
                isLatestVersion: true
              });
            }
          });
        });
      }, function (error) {
        log('An error occurred while checking cache status.', true);
        log(error, true);
        !isVerboseMode && setCacheStatus({
          loading: false,
          isLatestVersion: true
        });
      });

      return Promise.resolve(_temp2 && _temp2.then ? _temp2.then(function () { }) : void 0);
    } catch (e) {
      return Promise.reject(e);
    }
  }, [currentVersion, isVerboseMode, metaFileDirectory]);

  var isThereNewVersion = function isThereNewVersion(metaVersion, currentVersion) {
    if (reloadOnDowngrade) {
      return !compare(metaVersion, currentVersion, '=');
    }

    return compare(metaVersion, currentVersion, '>');
  };

  var refreshCacheAndReload = function refreshCacheAndReload() {
    try {
      return Promise.resolve(_catch(function () {
        var _temp3 = function () {
          var _window;

          if ((_window = window) !== null && _window !== void 0 && _window.caches) {
            var _window2 = window,
              caches = _window2.caches;
            return Promise.resolve(caches.keys()).then(function (cacheNames) {
              var cacheDeletionPromises = cacheNames.map(function (n) {
                return caches["delete"](n);
              });
              return Promise.resolve(Promise.all(cacheDeletionPromises)).then(function () {
                log('The cache has been deleted.');
                window.location.reload(true);
              });
            });
          }
        }();

        if (_temp3 && _temp3.then) return _temp3.then(function () { });
      }, function (error) {
        log('An error occurred while deleting the cache.', true);
        log(error, true);
      }));
    } catch (e) {
      return Promise.reject(e);
    }
  };

  if (!isEnabled) {
    return children;
  } else {
    if (cacheStatus.loading) {
      return loadingComponent;
    }

    if (!cacheStatus.loading && !cacheStatus.isLatestVersion) {
      if (onCacheClear) {
        onCacheClear(refreshCacheAndReload);
      } else {
        refreshCacheAndReload();
      }

      return null;
    }

    return React.createElement(CacheBusterContext.Provider, {
      value: {
        checkCacheStatus: checkCacheStatus
      }
    }, children);
  }
}

MyCacheBuster.propTypes = {
  children: PropTypes.element.isRequired,
  currentVersion: PropTypes.string.isRequired,
  isEnabled: PropTypes.bool.isRequired,
  isVerboseMode: PropTypes.bool,
  loadingComponent: PropTypes.element,
  metaFileDirectory: PropTypes.string,
  onCacheClear: PropTypes.func
};

var useCacheBuster = function useCacheBuster() {
  var context = useContext(CacheBusterContext);

  if (context === undefined || context === null) {
    throw new Error('useCacheBuster must be used within a MyCacheBuster component.');
  }

  return context;
};

export default MyCacheBuster;
export { useCacheBuster };
